import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { apiClient } from '@theraply/api-client';

// Types
export interface TherapistSearchFilters {
  location?: string; // lat,lng or address string
  latitude?: number;
  longitude?: number;
  radius?: number; // miles
  specialty?: string[]; // array of specialties
  sessionType?: 'in_person' | 'video' | 'both';
  minRate?: number;
  maxRate?: number;
  dayOfWeek?: number; // 0-6
  startTime?: string; // HH:mm
  endTime?: string; // HH:mm
  page?: number;
  limit?: number;
  sortBy?: 'distance' | 'rating' | 'rate';
}

export interface TherapistResult {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  bio?: string;
  profilePhotoS3Key?: string;
  specialties: string[];
  sessionTypes: string[];
  ratePerSession: number;
  rating: number;
  reviewCount: number;
  yearsOfExperience?: number;
  languages?: string[];
  city?: string;
  state?: string;
  distanceMiles?: number;
  nextAvailableSlot?: string;
}

export interface SearchResults {
  therapists: TherapistResult[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook for therapist search with debouncing and caching.
 *
 * Features:
 * - Automatic debouncing for search queries
 * - Query caching via React Query
 * - Pagination support
 * - Filter management
 */
export function useTherapistSearch(initialFilters: TherapistSearchFilters = {}) {
  const [filters, setFilters] = useState<TherapistSearchFilters>({
    page: 1,
    limit: 20,
    radius: 25,
    sortBy: 'distance',
    ...initialFilters,
  });

  // Debounce filters to prevent excessive API calls
  const debouncedFilters = useDebounce(filters, 500);

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    if (debouncedFilters.location) {
      params.append('location', debouncedFilters.location);
    } else if (debouncedFilters.latitude && debouncedFilters.longitude) {
      params.append('location', `${debouncedFilters.latitude},${debouncedFilters.longitude}`);
    }

    if (debouncedFilters.radius) {
      params.append('radius', debouncedFilters.radius.toString());
    }

    if (debouncedFilters.specialty && debouncedFilters.specialty.length > 0) {
      params.append('specialty', debouncedFilters.specialty.join(','));
    }

    if (debouncedFilters.sessionType) {
      params.append('sessionType', debouncedFilters.sessionType);
    }

    if (debouncedFilters.minRate !== undefined) {
      params.append('minRate', debouncedFilters.minRate.toString());
    }

    if (debouncedFilters.maxRate !== undefined) {
      params.append('maxRate', debouncedFilters.maxRate.toString());
    }

    if (debouncedFilters.dayOfWeek !== undefined) {
      params.append('dayOfWeek', debouncedFilters.dayOfWeek.toString());
    }

    if (debouncedFilters.startTime) {
      params.append('startTime', debouncedFilters.startTime);
    }

    if (debouncedFilters.endTime) {
      params.append('endTime', debouncedFilters.endTime);
    }

    if (debouncedFilters.page) {
      params.append('page', debouncedFilters.page.toString());
    }

    if (debouncedFilters.limit) {
      params.append('limit', debouncedFilters.limit.toString());
    }

    if (debouncedFilters.sortBy) {
      params.append('sortBy', debouncedFilters.sortBy);
    }

    return params.toString();
  }, [debouncedFilters]);

  // Search query
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<SearchResults>({
    queryKey: ['therapist-search', queryParams],
    queryFn: async () => {
      const response = await apiClient.get(`/therapists/search?${queryParams}`);
      return response.data;
    },
    enabled: !!queryParams, // Only run if we have filters
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<TherapistSearchFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 when filters change (except when explicitly setting page)
      page: newFilters.page !== undefined ? newFilters.page : 1,
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 20,
      radius: 25,
      sortBy: 'distance',
    });
  }, []);

  // Pagination helpers
  const goToPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const nextPage = useCallback(() => {
    if (data && data.pagination.page < data.pagination.totalPages) {
      goToPage(data.pagination.page + 1);
    }
  }, [data, goToPage]);

  const prevPage = useCallback(() => {
    if (data && data.pagination.page > 1) {
      goToPage(data.pagination.page - 1);
    }
  }, [data, goToPage]);

  return {
    // Data
    therapists: data?.therapists || [],
    pagination: data?.pagination,
    filters,

    // Loading states
    isLoading,
    isError,
    error,

    // Actions
    updateFilters,
    clearFilters,
    refetch,

    // Pagination
    goToPage,
    nextPage,
    prevPage,
    hasNextPage: data ? data.pagination.page < data.pagination.totalPages : false,
    hasPrevPage: data ? data.pagination.page > 1 : false,
  };
}

/**
 * Hook to fetch all available specialties.
 */
export function useSpecialties() {
  return useQuery<string[]>({
    queryKey: ['specialties'],
    queryFn: async () => {
      const response = await apiClient.get('/therapists/specialties');
      return response.data.specialties;
    },
    staleTime: 60 * 60 * 1000, // Cache for 1 hour (specialties don't change often)
  });
}

/**
 * Hook to find nearby therapists based on coordinates.
 */
export function useNearbyTherapists(
  latitude?: number,
  longitude?: number,
  radius: number = 25
) {
  return useQuery<TherapistResult[]>({
    queryKey: ['nearby-therapists', latitude, longitude, radius],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (latitude !== undefined && longitude !== undefined) {
        params.append('lat', latitude.toString());
        params.append('lng', longitude.toString());
        params.append('radius', radius.toString());
      }

      const response = await apiClient.get(`/therapists/nearby?${params.toString()}`);
      return response.data.therapists;
    },
    enabled: latitude !== undefined && longitude !== undefined,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to get user's current location.
 */
export function useUserLocation() {
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setIsLoading(false);
      },
      (error) => {
        setError(error.message);
        setIsLoading(false);
      }
    );
  }, []);

  return {
    location,
    isLoading,
    error,
    getCurrentLocation,
  };
}
